import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useFavoriteMentors } from '../../hooks/apiHooks/MentorHooks'
import { MentorBase } from '../../types/mentor'

export const useMentors = (currentPage: number) => {
  const queryClient = useQueryClient()

  const [mentors, setMentors] = useState<MentorBase[]>([])
  const [isInvalidating, setIsInvalidating] = useState(false)
  const [inDeleteProcess, setInDeleteProcess] = useState(false)
  const [isLoadedAll, setIsLoadedAll] = useState(false)
  const [dataIsEmpty, setDataIsEmpty] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [removableMentor, setRemovableMentor] = useState<string | null>(null)

  const { data, isLoading } = useFavoriteMentors(currentPage)

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: ['get-favorite-mentors'] })
    },
    [],
  )

  const removeMentor = (mentorId: string) => {
    if (!isLoadedAll) {
      setRemovableMentor(mentorId)
      setIsInvalidating(true)
      void queryClient.invalidateQueries({ queryKey: ['get-favorite-mentors', pageIndex] })
    } else {
      const filteredMentors = mentors.filter(
        mentor => mentor.mentorId !== mentorId,
      )
      setMentors(filteredMentors)
      setDataIsEmpty(!filteredMentors.length)
      setInDeleteProcess(false)
    }
  }

  useEffect(() => {
    if (data) {
      setIsLoadedAll(data ? data.total_page - 1 === data.page_index : false)
      setPageIndex(data.page_index)
      if (isInvalidating) {
        setMentors((prev: MentorBase[]) => {
          const filteredMentors = prev.filter(
            mentor => mentor.mentorId !== removableMentor,
          )
          setDataIsEmpty(!filteredMentors.length)
          const splitIndex = pageIndex * 15
          const firstPart = filteredMentors.slice(0, splitIndex)
          setTimeout(() => {
            setIsInvalidating(false)
            setInDeleteProcess(false)
            setRemovableMentor(null)
          })
          return [...firstPart, ...data.items]
        })
        return
      }
      setDataIsEmpty(data.totalItems === 0)
      setMentors((prev: MentorBase[]) => [...prev, ...data.items])
    }
  }, [data])

  return {
    mentors,
    isLoading,
    totalPages: data?.total_page || 0,
    setIsInvalidating,
    inDeleteProcess,
    setInDeleteProcess,
    isLoadedAll,
    dataIsEmpty,
    removeMentor,
  }
}
